// Our own bot, on every page of our own site: the Build Bots Now house bot,
// owned by hello@buildbotsnow.com and loaded with the same one-line snippet a
// customer pastes. On the public pages it answers visitors deciding whether to
// sign up; in the wizard and the dashboard it answers customers building a
// bot. Mounted once, in App, so moving between pages keeps the conversation.
//
// The show/hide bookkeeping below predates that and still earns its place: if
// anything ever renders this conditionally again, the launcher follows it,
// including one that only appears after the page that asked for it has gone.
//
// Not on a bot's own details page (showSiteAssistant): its bubble sat on top of
// the reply box on the Chats tab, and the owner there is working, not deciding.
// Leaving the page brings it back, with the conversation where it was.
//
// Only on buildbotsnow.com itself. The bot's allowed domains are that and www,
// so on auto-bots-1.web.app or a dev server it would draw a launcher that then
// refuses every question.

import { useEffect } from "react";
import { SITE_ASSISTANT_AGENT_ID } from "../lib/marketing";

export const SITE_ASSISTANT_SCRIPT_ID = "build-bots-now-site-assistant";
const SCRIPT_URL = "https://buildbotsnow.com/build-bots-now.js";
const SITE_HOSTS = ["buildbotsnow.com", "www.buildbotsnow.com"];

// A bot's details page is /agents/<id>; /agents/new is the wizard, which keeps
// the bubble.
export function showSiteAssistant(pathname: string): boolean {
  const match = /^\/agents\/([^/]+)\/?$/.exec(pathname);
  return !match || match[1] === "new";
}

type WidgetApi = { open?: (options: { agent?: string; message?: string }) => boolean };

// Open the house bot from a button on our own pages, with a first question
// already sent (widget BuildBotsNow.open). Returns false where the house bot
// does not run (not buildbotsnow.com), so the caller falls back to its link.
// A click before the widget script has loaded is answered once it has.
export function askSiteAssistant(message: string, hostname?: string): boolean {
  if (!SITE_HOSTS.includes(hostname ?? window.location.hostname)) return false;
  const open = () => (window as unknown as { BuildBotsNow?: WidgetApi }).BuildBotsNow?.open?.({ agent: SITE_ASSISTANT_AGENT_ID, message });
  if ((window as unknown as { BuildBotsNow?: WidgetApi }).BuildBotsNow?.open) {
    open();
    return true;
  }
  const script = document.getElementById(SITE_ASSISTANT_SCRIPT_ID);
  if (!script) return false;
  script.addEventListener("load", () => open(), { once: true });
  return true;
}

let wanted = 0;
let observer: MutationObserver | null = null;

function launchers(): HTMLElement[] {
  // The page watcher can fire once more after its page is gone (a test's
  // document torn down under it); nothing to show or hide then.
  if (typeof document === "undefined") return [];
  return Array.from(document.querySelectorAll<HTMLElement>(`[data-build-bots-now="${SITE_ASSISTANT_AGENT_ID}"]`));
}

function applyVisibility() {
  for (const host of launchers()) host.style.display = wanted > 0 ? "" : "none";
}

// hostname is for tests. It is read inside the effect, never during render,
// because Terms and Privacy are also rendered in Node at build time.
export default function SiteAssistant({ hostname }: { hostname?: string }) {
  useEffect(() => {
    if (!SITE_HOSTS.includes(hostname ?? window.location.hostname)) return;
    wanted += 1;
    if (!document.getElementById(SITE_ASSISTANT_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SITE_ASSISTANT_SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.setAttribute("data-agent", SITE_ASSISTANT_AGENT_ID);
      document.body.appendChild(script);
    }
    if (!observer && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(applyVisibility);
      observer.observe(document.body, { childList: true });
    }
    applyVisibility();
    return () => {
      wanted -= 1;
      applyVisibility();
    };
  }, [hostname]);

  return null;
}
